// Reservation Details Selectors

/**
 * THIS FILE IS NOW DEPRECATED AND WILL BE REMOVED IN THE NEXT MAJOR RELEASE.
 * PLEASE, DO NOT ADD ANY NEW CODE HERE.
 */
import get from 'lodash-es/get';
import sortBy from 'lodash-es/sortBy';
import { GwDatesWrapper } from '../../../components/_internal_date_/gwDatesWrapper';
import { EReservationStatus } from '../../../utilities/availabilityUtils';
import {
  CABANAS_CODES_TYPES,
  CHECKOUT_TIMES,
  DATE_FORMATS,
  DAY_PASSES_TYPE,
  HOWL_N_LEARN_CODE,
  LATE_CHECKOUT_PREFIXES
} from '../../../utilities/constants';

/**
 * Get lastName (users) for the reservation.
 * @return {String} - Arrival Date;
 */
export const getLastName = state => {
  return state.data?.contactInfo?.lastName ?? state.data?.lastName;
};

/**
 * Get firstName (users) for the reservation.
 * @return {String} - Reservation guest's first name;
 */
export const getFirstName = state => {
  return state.data?.contactInfo?.firstName ?? state.data?.firstName;
};

/**
 * Get check-in (arrival) date for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {String} - Arrival Date.
 */
export const getArrivalDate = state => get(state, 'data.arrival');

/**
 *
 * @param {Object} state reservationDetails entity store.
 * @returns {String} - Arrival Date as Date Object
 */
export const getArrivalDateObject = state => {
  const date = get(state, 'data.arrival');
  if (!date) return date;

  return GwDatesWrapper.format(date, DATE_FORMATS.default);
};

/**
 *
 * Get check-out (departure) date for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {String} - Departure Date.
 */
export const getDepartureDate = state => get(state, 'data.departure');

/**
 *
 * @param {Object} state reservationDetails entity store.
 * @returns {String} - Departure Date as Date Object
 */
export const getDepartureDateObject = state => {
  const date = get(state, 'data.departure');
  if (!date) return date;

  return GwDatesWrapper.format(date, DATE_FORMATS.default);
};

/**
 * Get the offer code added for the reservation.
 * @param {Object} state Reservations store.
 * @return {string} Confirmed reservation offer code.
 */
export const getOfferCode = state => get(state, 'data.offerCode');

/**
 * Get the id for the reservation.
 * @param {Object} state Reservations store.
 * @return {string} Confirmed reservation id.
 */
export const getReservationId = state => {
  return get(state, 'data.id') ?? get(state, 'data.confirmationId');
};

/**
 * Get the id for the reservation on APS.
 * @param {Object} state Reservations store.
 * @return {string} Confirmed reservation id.
 */
export const getReservationIdOnAps = state => {
  return get(state, 'data.externalId') ?? get(state, 'data.id');
};

/**
 * Get the reservation status.
 * @param {Object} state Reservations store.
 * @return {string} Confirmed reservation status.
 */
export const getStatus = state => get(state, 'data.status');

/**
 * Get the number of adults.
 * @param {Object} state Reservations store.
 * @return {number} Confirmed reservation number of adults.
 */
export const getAdults = state => {
  return state.data?.guests?.adults ?? state.data?.selection?.numAdults;
};

/**
 * Get the number of billable kids.
 * @param {Object} state Reservations store.
 * @return {number} Confirmed reservation number of billable kids.
 */
export const getBillableGuest = state => {
  const children = get(state, 'data.guests.children');
  const numChildrenOverTwo = get(state, 'data.selection.numChildrenOverTwo')
    ? get(state, 'data.selection.numChildrenOverTwo')
    : children
    ? children.filter(kid => kid > 2).length
    : 0;
  return getAdults(state) + numChildrenOverTwo;
};

/**
 * Get the number of children.
 * @param {Object} state Reservations store.
 * @return {number} Confirmed reservation number of children.
 */
export const getKids = state => {
  return state.data?.guests?.children?.length ?? state.data?.selection?.numChildren ?? 0;
};

/* Get the selected ages of kids for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {array} - kids ages
 */
export const getKidsAges = state => {
  return state.data?.guests?.children ?? state.data?.children ?? [];
};

/**
 * Get the room type.
 * @param {Object} state Reservations store.
 * @return {string} Confirmed reservation room type.
 */
export const getRoomType = state => {
  return get(state, 'data.product.code') ?? get(state, 'data.roomType');
};

/**
 * Get the property.
 * @param {Object} state Reservations store.
 * @return {string} Confirmed reservation property.
 */
export const getProperty = state => {
  return get(state, 'data.lodge.code') ?? get(state, 'data.property');
};

/**
 * Get the number of nights for reservation.
 * @param {Object} state Reservations store.
 * @return {number} Confirmed reservation number of nights.
 */
export const getNumberOfNights = state => {
  return (
    get(state, 'data.selection.numberOfNights') ??
    GwDatesWrapper.diff(getDepartureDate(state), getArrivalDate(state), 'days')
  );
};

/**
 * Get sutie details for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {Object} - Suite details[Suite name, adults count, kids count, infants count, offer code] of the confirmed suite.
 */
export const getSuiteDetails = (state, includePropertyAndRoomType) => {
  const reservation = get(state, 'data');
  let suiteDetail = {};
  if (reservation) {
    if (reservation?.isCabana) {
      suiteDetail.title = reservation.roomDescription;
    }

    // Adding total room rate for selected suite
    if (reservation?.rateInfo) {
      const { totalRoomRate } = reservation.rateInfo;
      suiteDetail.totalRoomRate = totalRoomRate;
    } else {
      suiteDetail.totalRoomRate = reservation?.product?.totalPrice;
    }

    // Setting guests count.
    if (reservation?.selection) {
      const { numAdults, numChildrenOverTwo, numChildren } = reservation.selection;
      suiteDetail.adults = numAdults;
      suiteDetail.kids = numChildrenOverTwo;
      suiteDetail.totalKids = numChildren;
    } else {
      suiteDetail.adults = reservation?.guests?.adults;
      suiteDetail.kids = reservation?.guests?.children ? reservation.guests.children.filter(kid => kid > 2).lenght : 0;
      suiteDetail.totalKids = reservation?.guests?.children ? reservation.guests.children.length : 0;
    }

    suiteDetail.offerCode = reservation.offerCode;
    if (includePropertyAndRoomType) {
      suiteDetail.property = reservation.property || reservation?.lodge?.code;
      suiteDetail.roomType = reservation.roomType;
    }
  }
  return suiteDetail;
};

/**
 * Get the total room rate.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Total room rate of confirmed suite for the reservation.
 */
export const getTotalRoomRate = state => {
  return get(state, 'data.costSummary.subtotal') ?? get(state, 'data.rateInfo.totalRoomRate') ?? 0;
};

/**
 * Get the taxes amount including cabanas.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Taxes amount for the reservation including cabanas.
 */
export const getTaxesAmountWithCabanas = state => {
  const taxesAmount = get(state, 'data.costSummary.taxesAndFees.TAX') ?? get(state, 'data.rateInfo.taxes');

  const cabanas = get(state, 'data.cabanas');
  let totalTaxesCabanas = 0;
  if (cabanas) {
    totalTaxesCabanas = cabanas
      .filter(c => c.status !== EReservationStatus.CANCELED)
      .map(c => c.totalTaxes)
      .reduce((total, roomRate) => {
        return total + roomRate;
      }, 0);
  }
  return taxesAmount + totalTaxesCabanas;
};

/**
 * Get the resort fee for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Resort fee for the reservation.
 */
export const getResortFee = state => {
  return get(state, 'data.costSummary.taxesAndFees.RESORT') ?? get(state, 'data.rateInfo.resortFee');
};

/**
 * Get the parking fee for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Parking fee for the reservation.
 */
export const getParkingFee = state => {
  return get(state, 'data.costSummary.taxesAndFees.PARKING') ?? get(state, 'data.rateInfo.parkingFee');
};

/**
 * Get the Sustainability  fee for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Resort fee for the reservation.
 */
export const getSustainabilityFee = state => {
  return get(state, 'data.costSummary.taxesAndFees.SUSTAINABILITY') ?? get(state, 'data.rateInfo.sustainabilityFee');
};

/**
 * Get the summary total for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Summary total for the reservation.
 */
export const getSummaryTotal = state => {
  return get(state, 'data.costSummary.total') ?? get(state, 'data.rateInfo.total');
};

/**
 * Gets the summary total for the reservation including cabanas.
 * @param {Object} state reservationDetails entity store.
 * @returns {number} - Summary total for the reservation including cabanas.
 */
export const getSummaryTotalWithCabana = state => {
  const cabanas = get(state, 'data.cabanas');
  let totalCabanas = 0;
  if (cabanas) {
    totalCabanas = cabanas
      .filter(c => c.status !== EReservationStatus.CANCELED)
      .map(c => c.total)
      .reduce((total, totalCabanaWithTaxes) => {
        return total + totalCabanaWithTaxes;
      }, 0);
  }
  return getSummaryTotal(state) + totalCabanas;
};

/**
 * Get total room rate plus packages rate.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Total room rate plus packages.
 */
export const getTotalRoomRateAndPackages = state => {
  return (
    get(state, 'data.costSummary.totalRoomRateAndPackages', 0) ??
    get(state, 'data.rateInfo.totalRoomRateAndPackages', 0)
  );
};

/**
 * Get the package total for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Total amount of the packages selected.
 */
export const getPackageTotal = state => {
  return getTotalRoomRateAndPackages(state) - getTotalRoomRate(state);
};

/**
 * Get the package total with cabanas for the reservation.
 * @param {Object} state reservationDetails entity store
 * @returns {number} Total sum of amounts for packages including cabanas
 */
export const getPackageTotalWithCabanas = state => {
  const cabanas = get(state, 'data.cabanas');
  let totalCabanas = 0;
  if (cabanas) {
    totalCabanas = cabanas
      .filter(c => c.status !== EReservationStatus.CANCELED)
      .map(c => c.totalRoomRate)
      .reduce((total, roomRate) => {
        return total + roomRate;
      }, 0);
  }
  return getPackageTotal(state) + totalCabanas;
};

export const getCostSummarySuiteName = state => {
  return get(state, 'data.product.name');
};

/**
 * Get the deposit paid for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - deposit paid for the reservation.
 */
export const getDepositePaid = state => {
  const roomDeposit = get(state, 'data.costSummary.depositPaid')
    ? get(state, 'data.costSummary.depositPaid')
    : get(state, 'data.advancedDeposit', get(state, 'data.rateInfo.advancedDeposit', 0));
  const cabanaDeposit = get(state, 'data.cabanasDepositAmount') ? get(state, 'data.cabanasDepositAmount') : 0;
  return roomDeposit + cabanaDeposit;
};

/**
 * Get the room deposit paid for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - deposit paid for the reservation.
 */
export const getRoomDeposit = state => {
  const roomDeposit = get(state, 'data.advancedDeposit', get(state, 'data.rateInfo.advancedDeposit', 0));

  return get(state, 'data.costSummary.depositPaid') ? get(state, 'data.costSummary.depositPaid') : roomDeposit;
};

/**
 * Get the site for reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {string} Confirmed reservation site.
 */
export const getSite = state => {
  return get(state, 'data.lodge.code') ?? get(state, 'data.selection.site');
};

/**
 * Get packages added to the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {array.<Object>} List of packages added.
 */
export const getPackages = state => {
  const newAPI = get(state, 'data.addOns', []);
  const oldAPI = get(state, 'data.packages', []);
  return newAPI.length > 0 ? get(state, 'data.addOns', []) : oldAPI.length > 0 ? get(state, 'data.packages', []) : [];
};

/**
 * Get cabanas added in the same days of the reservation.
 * @param {Object} state reservationDetails entity store.
 * @returns {array.<Object>} List of cabanas added in the same days of the reservation.
 */
export const getCabanas = state => {
  const cabanasList =
    state?.data?.addOns && state.data.addOns.find(item => item.type === 'favorite')
      ? state.data.addOns.filter(item => item.type === 'favorite')
      : get(state, 'data.cabanas', []);
  return cabanasList || [];
};

/**
 * Get the display name for confirmed reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {string} Confirmed reservation display name.
 */
export const getDisplayName = state => {
  return get(state, 'data.lodge.name') ?? get(state, 'data.selection.siteDisplayName');
};

/**
 * Get the value of isLoading.
 * @param {Object} state reservationDetails entity store.
 * @return {boolean} True if we are fetching data.
 */
export const getIsLoading = state => get(state, 'isLoading');

/**
 * Get the error message.
 * @param {Object} state ReservationDetails entity store.
 * @return {string} Error message
 */
export const getError = state => get(state, 'errorMessage');

/**
 * Get the checkout time selected for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {String} - String containing the checkout time.
 */
export const getCheckoutTime = state => {
  const reservation = get(state, 'data');
  let checkoutTime = CHECKOUT_TIMES.AM_11;
  if (reservation) {
    if (reservation?.addOns?.length > 0) {
      const lateCheckoutPkg = reservation.addOns.find(
        pkg => pkg.code?.includes(LATE_CHECKOUT_PREFIXES.code) || pkg.code?.includes(HOWL_N_LEARN_CODE)
      );

      if (lateCheckoutPkg) {
        checkoutTime = CHECKOUT_TIMES.PM_2;
      }
    }
    const { packages } = reservation;
    if (packages) {
      const lateCheckoutPkg = packages.find(
        pkg => pkg.packageCode.includes(LATE_CHECKOUT_PREFIXES.code) || pkg.packageCode.includes(HOWL_N_LEARN_CODE)
      );

      if (lateCheckoutPkg) {
        checkoutTime = CHECKOUT_TIMES.PM_2;
      }
    }
  }
  return checkoutTime;
};

/**
 * Get the whole reservation object
 * @param {Object} state
 */
export const getReservation = state => {
  return get(state, 'data');
};

/**
 * Get the due at Checking amount for the reservation including cabanas.
 * @param {Object} state reservationDetails entity store.
 * @returns {number} - Due at Checkin amount for the reservation.
 */
export const getDueAtCheckinAmountWithCabanas = state => {
  const totalWithCabana = Number(getSummaryTotalWithCabana(state).toFixed(2));
  const totalDepositPaid = Number(getDepositePaid(state).toFixed(2));
  return totalWithCabana - totalDepositPaid;
};

/**
 * Types of reservations.
 * @enum {string}
 */
export const RESERVATION_TYPES = {
  suite: 'suite',
  dayPass: 'dayPass',
  cabana: 'cabana'
};
/**
 * Returns if is a suite, day pass or a cabana reservation.
 * @param {Object} state Reservations store.
 * @return {String} Type of reservation
 */
export const getReservationType = state => {
  const roomType = getRoomType(state);
  const dayPassesCodes = Object.values(DAY_PASSES_TYPE);
  const cabanaCodes = Object.values(CABANAS_CODES_TYPES);
  if (dayPassesCodes.includes(roomType)) {
    return RESERVATION_TYPES.dayPass;
  } else if (cabanaCodes.includes(roomType)) {
    return RESERVATION_TYPES.cabana;
  }

  return RESERVATION_TYPES.suite;
};

/**
 * Get the special request from reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {array.<Object>} List of special request added.
 */
export const getSpecialRequests = state => get(state, 'data.specialRequest');

/**
 * Get the special request from reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {Object} Birth Day Celebration Special Request.
 */
export const getBirthDayCelebration = state => get(state, 'data.birthDayCelebration');

/**
 * Get comments from reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {array.<Object>} List of comments.
 */
export const getComments = state => get(state, 'data.comments');

export const getCabanasAsPackages = state => {
  // Check if this is cabana object is present.
  const cabanasEntity = get(state, 'data.cabanas', []);
  if (!cabanasEntity) {
    return;
  }

  const cabanasFiltered = cabanasEntity.filter(c => c.status !== EReservationStatus.CANCELED);
  const cabanas = sortBy(cabanasFiltered, item => GwDatesWrapper.format(item.insertedAt, DATE_FORMATS.numberFormat), [
    'asc'
  ]);
  return cabanas.reduce((cabanasAcc, currentCabana, index) => {
    const cabanaToAdd = {
      description: currentCabana.roomDescription,
      shortDescription: currentCabana.roomDescription,
      endDate: currentCabana.arrival,
      startDate: currentCabana.arrival,
      packageAmount: currentCabana.totalRoomRate,
      packageCode: index === cabanas.length - 1 ? currentCabana.roomType : `${currentCabana.roomType}_${index}`,
      quantity: 1,
      tax: currentCabana.totalTaxes,
      totalAmount: currentCabana.total,
      unitAmount: currentCabana.totalRoomRate,
      isCabana: true,
      totalRoomRateAndPackages: currentCabana.totalRoomRateAndPackages
    };
    cabanasAcc.push(cabanaToAdd);
    return cabanasAcc;
  }, []);
};

export const getIsFullDayPass = state => {
  return get(state, 'data.product.code') === DAY_PASSES_TYPE.fullDay;
};

export const getSuppressRate = state =>
  get(state, 'data.costSummary.suppressRate') ?? get(state, 'data.rateInfo.suppressRate');

export const getCabanaDeposit = state => {
  const cabanaDepositEntry = get(state, 'data.cabanasDepositAmount');

  return cabanaDepositEntry;
};

export const getPointsDiscountApplied = state =>
  get(state, 'data.costSummary.totalPoints') ?? get(state, 'data.rateInfo.totalPoints');

export const getOffersDiscountApplied = state =>
  get(state, 'data.costSummary.totalOffers') ?? get(state, 'data.rateInfo.totalOffers');

export const getReservationAddress = state => {
  return state.data?.address || state.data?.payment.billingAddress || {};
};

export const getCanAddGuestsFlag = state => {
  return state?.data?.canAddGuests;
};

export const getCanChangeDates = state => {
  return state?.data?.canChangeDates;
};

/**
 * Get the number of adults and kids.
 * @param {Object} state Reservations store.
 * @return {number} Confirmed reservation number of adults and kids.
 */
export const getTotalBillAndNonBillGuest = state => {
  return getAdults(state) + getKids(state);
};

export const getPackageErrorMessage = state => {
  return state.packageErrorMessageData;
};

export const getAddGuestReservationErrorMessage = state => {
  return state.addGuestReservationErrorMessage;
};

export const getRateType = state => {
  return state?.data?.rateType || '';
};

export const getRoomUpgradeInfo = state => {
  return state?.data?.roomUpgrade;
};

export const getTaxesAndFeesForReservation = state => {
  return state?.data?.taxesAndFees;
};

export const getPrivateLodgingFee = state => {
  return state?.data?.costSummary?.taxesAndFees?.PRIVATE_LODGING || state?.data?.rateInfo?.privateLodgingFee;
};

export const getContactInfo = state => ({
  firstName: state?.data?.firstName,
  lastName: state?.data?.lastName,
  email: state?.data?.email,
  phoneNumber: state?.data?.phoneNumber
});

export const getIsCabanaLoading = state => {
  return state.isCabanaLoading;
};

export const getCabanaErrorMessage = state => {
  return state.cabanaErrorMessage;
};

export const getCabanaRequestSuccess = state => {
  return state.cabanaRequestSuccess;
};

export const getIsDynamicOfferCode = state => {
  return state?.data?.isDynamicOfferCode ?? false;
};
